import React, { useEffect, useMemo } from 'react';
import { Group, Rect, Shape } from 'react-konva';

import { useFontsVersion } from '../../config';
import { getElementNodeId } from '../../core/documentSchema';
import { clearTextMetricsCache, drawTextLayout, layoutText } from './textLayout';
import { drawBackdropBlur } from './backdropBlur';

function TextElement({
  element,
  interactive,
  listening = true,
  hidden = false,
  onSelect,
  onContextMenu,
  onDragStart,
  onDragMove,
  onDragEnd,
  onTransform,
  onTransformEnd,
  onDblClick,
  onMeasure,
}) {
  const fontsVersion = useFontsVersion();
  const background = element.background || {};
  const shadow = element.shadow || {};
  const outline = element.outline || {};

  const metricsKey = useMemo(() => {
    clearTextMetricsCache();
    return fontsVersion;
  }, [fontsVersion]);

  const layout = useMemo(() => {
    return layoutText(element);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    element.runs,
    element.text,
    element.fontFamily,
    element.fontSize,
    element.fill,
    element.bold,
    element.italic,
    element.underline,
    element.strike,
    element.lineHeight,
    element.letterSpacing,
    element.align,
    element.width,
    background.enabled,
    background.padding,
    metricsKey,
  ]);

  useEffect(() => {
    if (onMeasure && layout.height > 0 && Math.abs(layout.height - (element.height || 0)) > 1) {
      onMeasure(element.id, { height: layout.height });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [element.id, layout.height, onMeasure]);

  if (!element.visible) {
    return null;
  }

  const boxWidth = element.width;
  const boxHeight = element.height;

  return (
    <Group
      id={getElementNodeId(element.id)}
      x={element.x}
      y={element.y}
      offsetX={boxWidth / 2}
      offsetY={boxHeight / 2}
      width={boxWidth}
      height={boxHeight}
      rotation={element.rotation || 0}
      scaleX={element.scaleX ?? 1}
      scaleY={element.scaleY ?? 1}
      opacity={hidden ? 0 : element.opacity ?? 1}
      listening={listening && !hidden}
      draggable={interactive}
      onClick={(event) => onSelect?.(element.id, event)}
      onTap={(event) => onSelect?.(element.id, event)}
      onDblClick={(event) => onDblClick?.(element, event)}
      onDblTap={(event) => onDblClick?.(element, event)}
      onContextMenu={(event) => onContextMenu?.(element, event)}
      onDragStart={() => onDragStart?.(element.id)}
      onDragMove={interactive ? (event) => onDragMove?.(element, event) : undefined}
      onDragEnd={(event) => onDragEnd?.(element.id, event)}
      onTransform={interactive ? (event) => onTransform?.(element.id, event) : undefined}
      onTransformEnd={(event) => onTransformEnd?.(element.id, event)}>
      {background.enabled && background.blur > 0 ? (
        <Shape
          name='ms-text-backdrop'
          width={boxWidth}
          height={boxHeight}
          listening={false}
          perfectDrawEnabled={false}
          sceneFunc={(context, shape) =>
            drawBackdropBlur(
              context._context,
              shape.width(),
              shape.height(),
              background.cornerRadius || 0,
              background.blur,
            )
          }
        />
      ) : null}
      {background.enabled ? (
        <Rect
          name='ms-text-bg'
          width={boxWidth}
          height={boxHeight}
          fill={background.fill}
          opacity={background.opacity ?? 1}
          cornerRadius={background.cornerRadius || 0}
          listening={false}
        />
      ) : null}
      <Shape
        name='ms-text'
        width={boxWidth}
        height={boxHeight}
        sceneFunc={(context, shape) => {
          const liveLayout = shape.width() === element.width ? layout : layoutText(element, shape.width());
          drawTextLayout(context._context, liveLayout, outline);
        }}
        hitFunc={(context, shape) => {
          context.beginPath();
          context.rect(0, 0, shape.width(), shape.height());
          context.closePath();
          context.fillShape(shape);
        }}
        shadowEnabled={Boolean(shadow.enabled)}
        shadowColor={shadow.color}
        shadowBlur={shadow.blur || 0}
        shadowOffsetX={shadow.offsetX || 0}
        shadowOffsetY={shadow.offsetY || 0}
        shadowOpacity={shadow.opacity ?? 1}
        perfectDrawEnabled={false}
      />
    </Group>
  );
}

export default TextElement;
